/**
 * Central mapping of backend status values → Portuguese labels + Tailwind classes.
 *
 * The DB keeps English values (`scheduled`, `done`, `no_show`, etc). This module
 * only defines how those values are DISPLAYED in the UI. Add new entries here
 * whenever a new status is introduced anywhere in the app.
 */

const FALLBACK = { label: null, cls: "border-border text-muted-foreground bg-muted" };

/** Applications (aplicações injetáveis) — DB: scheduled | done | cancelled | missed | no_show */
export const APPLICATION_STATUS = {
  scheduled: { label: "Agendada", cls: "border-primary/40 text-primary bg-primary/5" },
  done: { label: "Realizada", cls: "border-success/40 text-success bg-success/5" },
  cancelled: { label: "Cancelada", cls: "border-destructive/40 text-destructive bg-destructive/5" },
  missed: { label: "Não compareceu", cls: "border-warning/40 text-warning bg-warning/5" },
  no_show: { label: "Não compareceu", cls: "border-warning/40 text-warning bg-warning/5" },
};

/** Appointments (agendamentos) — DB: scheduled | confirmed | completed | cancelled | no_show | missed */
export const APPOINTMENT_STATUS = {
  scheduled: { label: "Agendado", cls: "border-primary/30 text-primary bg-primary/5" },
  confirmed: { label: "Confirmado", cls: "border-success/40 text-success bg-success/5" },
  completed: { label: "Concluído", cls: "border-border text-muted-foreground bg-muted" },
  cancelled: { label: "Cancelado", cls: "border-destructive/30 text-destructive bg-destructive/5" },
  missed: { label: "Não compareceu", cls: "border-warning/40 text-warning bg-warning/5" },
  no_show: { label: "Não compareceu", cls: "border-warning/40 text-warning bg-warning/5" },
};

/** Exames laboratoriais — DB: pending_review | reviewed */
export const EXAM_STATUS = {
  pending_review: { label: "Pendente", cls: "border-warning/40 text-warning bg-warning/5" },
  reviewed: { label: "Analisado", cls: "border-success/40 text-success bg-success/5" },
};

/** Emails da fila — DB: queued | sent | failed | bounced */
export const EMAIL_STATUS = {
  queued: { label: "Na fila", cls: "border-primary/30 text-primary bg-primary/5" },
  sent: { label: "Enviado", cls: "border-success/40 text-success bg-success/5" },
  failed: { label: "Falhou", cls: "border-destructive/40 text-destructive bg-destructive/5" },
  bounced: { label: "Rejeitado", cls: "border-warning/40 text-warning bg-warning/5" },
};

/** Protocol history — DB: replaced | completed | archived | closed */
export const PROTOCOL_HISTORY_STATUS = {
  replaced: { label: "Substituído", cls: "border-border text-muted-foreground bg-muted" },
  completed: { label: "Concluído", cls: "border-success/40 text-success bg-success/5" },
  archived: { label: "Arquivado", cls: "border-border text-muted-foreground bg-muted" },
  closed: { label: "Encerrado", cls: "border-border text-muted-foreground bg-muted" },
};

/** Clinical patterns — DB: draft | active | disabled */
export const PATTERN_STATUS = {
  draft: { label: "Rascunho", cls: "border-warning/40 text-warning bg-warning/5" },
  active: { label: "Ativo", cls: "border-success/40 text-success bg-success/5" },
  disabled: { label: "Desabilitado", cls: "border-border text-muted-foreground bg-muted" },
};

const REGISTRY = {
  application: APPLICATION_STATUS,
  appointment: APPOINTMENT_STATUS,
  exam: EXAM_STATUS,
  email: EMAIL_STATUS,
  protocol_history: PROTOCOL_HISTORY_STATUS,
  pattern: PATTERN_STATUS,
};

/**
 * Resolve a status value to { label, cls }. Falls back to the raw value with a
 * neutral style if the (entity, status) pair is unknown, so nothing ever renders
 * blank — only worst case: raw English tag (which becomes a bug marker to fix).
 */
export function getStatusLabel(entity, status) {
  if (!status) return { label: "—", cls: FALLBACK.cls };
  const key = String(status).toLowerCase();
  const entry = (REGISTRY[entity] || {})[key];
  if (entry) return entry;
  return { label: String(status), cls: FALLBACK.cls };
}
